import { useTranslation } from '@mukil/i18n';
import {
  Alert,
  Button,
  Checkbox,
  GlassCard,
  LoadingSpinner,
  StatTile,
} from '@jumpstone/ui-react';
import type { MigrationIssue } from '@jumpstone/core';
import type { MigrationReport } from '../../../data/use_wagepro_migration';
import { migrationIssueMessageKey } from './migration_issue_messages';

/**
 * Step 5 — Review: the server's validation report. Counts as StatTiles, every
 * issue in one table with a plain-language message resolved client-side from
 * its `code`+`params`, and a warning-acknowledgement gate before Import.
 * JSX-only; the import gate (blocking / unacknowledged warnings) is computed by
 * the screen.
 */
export function MigrationReviewStep({
  report,
  loading,
  warningCount,
  acknowledgeWarnings,
  onAcknowledgeChange,
  canImport,
  onBack,
  onImport,
}: {
  report: MigrationReport | null;
  loading: boolean;
  warningCount: number;
  acknowledgeWarnings: boolean;
  onAcknowledgeChange: (value: boolean) => void;
  canImport: boolean;
  onBack: () => void;
  onImport: () => void;
}): React.JSX.Element {
  const { t } = useTranslation('wp');

  if (loading) {
    return (
      <GlassCard className="rounded-2xl p-5">
        <div className="flex items-center gap-3">
          <LoadingSpinner />
          <span className="text-sm text-[color:var(--ink-muted)]">
            {t('migration.review.validating')}
          </span>
        </div>
      </GlassCard>
    );
  }

  if (!report) {
    return (
      <GlassCard className="rounded-2xl p-5">
        <p className="text-sm text-[color:var(--ink-muted)]">
          {t('migration.review.no_report')}
        </p>
        <div className="mt-4 flex justify-start">
          <Button variant="secondary" onClick={onBack}>
            {t('migration.button.back')}
          </Button>
        </div>
      </GlassCard>
    );
  }

  const errorCount = report.issues.filter((i) => i.severity === 'error').length;

  return (
    <div className="flex flex-col gap-4">
      <GlassCard className="rounded-2xl p-5">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatTile
            kicker={t('migration.counts.employees_create')}
            value={String(report.counts.employees_create)}
          />
          <StatTile
            kicker={t('migration.counts.employees_link')}
            value={String(report.counts.employees_link)}
          />
          <StatTile
            kicker={t('migration.counts.pay_periods')}
            value={String(report.counts.pay_periods)}
          />
          <StatTile
            kicker={t('migration.counts.leave_rows')}
            value={String(report.counts.leave_rows)}
          />
        </div>
      </GlassCard>

      {report.blocking ? (
        <Alert variant="danger" title={t('migration.review.blocked_title')}>
          {t('migration.review.blocked_body', { count: errorCount })}
        </Alert>
      ) : warningCount > 0 ? (
        <Alert variant="warning" title={t('migration.review.warnings_title')}>
          {t('migration.review.warnings_body', { count: warningCount })}
        </Alert>
      ) : (
        <Alert variant="success" title={t('migration.review.ready_title')}>
          {t('migration.review.ready_body')}
        </Alert>
      )}

      {report.issues.length > 0 && (
        <GlassCard className="overflow-x-auto rounded-2xl p-0">
          <table className="nv-table w-full">
            <thead>
              <tr>
                <th>{t('migration.review.col_sheet')}</th>
                <th>{t('migration.review.col_row')}</th>
                <th>{t('migration.review.col_severity')}</th>
                <th>{t('migration.review.col_message')}</th>
              </tr>
            </thead>
            <tbody>
              {report.issues.map((issue, index) => (
                <IssueRow key={index} issue={issue} />
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}

      {!report.blocking && warningCount > 0 && (
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={acknowledgeWarnings}
            onCheckedChange={(v) => {
              onAcknowledgeChange(v);
            }}
          />
          {t('migration.review.acknowledge')}
        </label>
      )}

      <div className="flex justify-between">
        <Button variant="secondary" onClick={onBack}>
          {t('migration.button.back')}
        </Button>
        <Button disabled={!canImport} onClick={onImport}>
          {t('migration.button.import')}
        </Button>
      </div>
    </div>
  );
}

function IssueRow({ issue }: { issue: MigrationIssue }): React.JSX.Element {
  const { t } = useTranslation('wp');
  const message = t(migrationIssueMessageKey(issue.code), {
    ...(issue.params ?? {}),
  });
  return (
    <tr>
      <td>{sheetLabel(t, issue.sheet)}</td>
      <td className="num">{issue.row > 0 ? issue.row : '—'}</td>
      <td>{severityLabel(t, issue.severity)}</td>
      <td>{message}</td>
    </tr>
  );
}

type TFn = (key: string, params?: Record<string, string | number>) => string;

/** Literal `t()` calls (not a template key) so the i18n scanner sees them. */
function severityLabel(t: TFn, severity: MigrationIssue['severity']): string {
  if (severity === 'error') return t('migration.severity.error');
  if (severity === 'warning') return t('migration.severity.warning');
  return t('migration.severity.info');
}

function sheetLabel(t: TFn, sheet: MigrationIssue['sheet']): string {
  // Literal `t()` calls (not a template key) so the i18n scanner sees them.
  if (sheet === 'Employees') return t('migration.sheet.Employees');
  if (sheet === 'Salary_History') return t('migration.sheet.Salary_History');
  if (sheet === 'Payroll_History') return t('migration.sheet.Payroll_History');
  if (sheet === 'Leave_Balances') return t('migration.sheet.Leave_Balances');
  return t('migration.sheet.file');
}
